import { useState, useCallback } from 'react';
import { type GeneratedAddress } from '@/types/database';

interface FuzzySearchResult {
  results: GeneratedAddress[];
  isLoading: boolean;
  error: string | null;
  searchAddresses: (query: string) => Promise<void>;
}

const liberiaApiBaseUrl = import.meta.env.VITE_LIBERIA_API_BASE_URL?.replace(/\/$/, '');

const toNullableString = (value: unknown) => {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
};

const mapLiberiaApiAddress = (address: any): GeneratedAddress => {
  const addressSource =
    address?.addressSource === 'official'
      ? 'google'
      : address?.addressSource === 'new'
        ? 'qls'
        : (address?.addressSource || 'qls');

  const latitude = Number(address?.latitude ?? address?.coordinates?.latitude ?? 0);
  const longitude = Number(address?.longitude ?? address?.coordinates?.longitude ?? 0);

  return {
    id: toNullableString(address?.id) || '',
    user_id: toNullableString(address?.userId) || '',
    address_source: addressSource,
    source_id: toNullableString(address?.sourceId) || '',
    formatted_address: toNullableString(address?.formattedAddress) || '',
    building_number: toNullableString(address?.buildingNumber) || '',
    street_name: toNullableString(address?.streetName) || '',
    city: toNullableString(address?.city) || '',
    district: toNullableString(address?.district) || '',
    county: toNullableString(address?.county) || '',
    state: toNullableString(address?.state) || '',
    country: toNullableString(address?.country) || '',
    postal_code: toNullableString(address?.postalCode) || '',
    zipcode: toNullableString(address?.zipcode),
    plus_code: toNullableString(address?.plusCode),
    coordinates: address?.coordinates || { latitude, longitude },
    geobloc: toNullableString(address?.geobloc),
    geobloc_id: toNullableString(address?.geoblocId),
    latitude,
    longitude,
    structure_type: toNullableString(address?.structureType) || '',
    structure_name: toNullableString(address?.structureName),
    structure_description: toNullableString(address?.structureDescription),
    additional_info: toNullableString(address?.additionalInfo),
    raw_source_data: address?.rawSourceData || {},
    location_types: address?.locationTypes || null,
    google_maps_url: null,
    validation_status: address?.validationStatus || 'pending',
    validated_by: null,
    validated_at: null,
    project_id: toNullableString(address?.projectId),
    created_at: address?.createdAt || new Date().toISOString(),
    updated_at: address?.updatedAt || new Date().toISOString(),
  };
};

export const useFuzzySearch = (): FuzzySearchResult => {
  const [results, setResults] = useState<GeneratedAddress[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const searchAddresses = useCallback(async (query: string) => {
    if (!query.trim() || query.length < 3) {
      setResults([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      if (import.meta.env.DEV) {
        console.log('Secure fuzzy search for:', query);
      }

      if (!liberiaApiBaseUrl) {
        throw new Error('QLS API is not configured');
      }

      const response = await fetch(`${liberiaApiBaseUrl}/api/v1/search/addresses`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query: query.trim(),
          limit: 20,
        }),
      });

      const payload = await response.json().catch(() => null);

      if (!response.ok) {
        throw new Error(payload?.error?.message || 'Address search failed');
      }

      const mappedResults = Array.isArray(payload?.data)
        ? payload.data.map(mapLiberiaApiAddress)
        : [];

      setResults(mappedResults);

      if (mappedResults.length === 0) {
        setError('No results found. Please try a more specific search.');
      } else {
        setError(null);
      }

    } catch (err) {
      console.error('Search error:', err);

      let userFriendlyMessage = 'Search service is temporarily unavailable. Please try again in a moment.';

      const errorMessage = err instanceof Error ? err.message : String(err);

      if (errorMessage.includes('Rate limit exceeded')) {
        userFriendlyMessage = 'Too many searches. Please wait a moment before searching again.';
      } else if (errorMessage.includes('too broad') || errorMessage.includes('more specific')) {
        userFriendlyMessage = 'Please be more specific in your search (include building number, street name, and city).';
      } else if (errorMessage.includes('FunctionsHttpError') || errorMessage.includes('non-2xx status')) {
        userFriendlyMessage = 'Search service is temporarily down. Please try again later.';
      } else if (errorMessage.includes('timeout') || errorMessage.includes('network')) {
        userFriendlyMessage = 'Connection timeout. Please check your internet connection and try again.';
      } else if (errorMessage.includes('authorization') || errorMessage.includes('unauthorized')) {
        userFriendlyMessage = 'Authentication required. Please sign in to search addresses.';
      }
      
      setError(userFriendlyMessage);
      setResults([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    results,
    isLoading,
    error,
    searchAddresses
  };
};
